import React, { useState, useEffect, useRef } from "react";
import Select, { components, createFilter } from "react-select";
import { LocalizationProvider, DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fr } from "date-fns/locale";
import { useTranslation } from 'react-i18next';
import searchIcon from "../icons/search.svg";
import EyeIcon from "../icons/eyeg.svg";
import EyeOffIcon from "../icons/eye-offg.svg";
import calendarIcon from "../icons/calendar.svg";
import { formatAmountForm } from "../utils/format";

const RANGE_BG = '#F2F2F4';
const RANGE_ACCENT = '#3872B0';
const RANGE_ACCENT_HOVER = '#306196';

const RangeDay = ({ day, outsideCurrentMonth, startDate, endDate, ...other }) => {
  const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const isStart = sameDay(day, startDate);
  const isEnd = sameDay(day, endDate);
  const hasRange = !!(startDate && endDate);
  const isInRange = hasRange && day > startDate && day < endDate;
  const isSingleDay = isStart && isEnd;
  const weekday = day.getDay(); // 0 = Sunday, 6 = Saturday
  const showRangeBg = !isSingleDay && (isInRange || (isStart && hasRange) || (isEnd && hasRange));

  const roundLeft = weekday === 0 || isStart;
  const roundRight = weekday === 6 || isEnd;

  return (
    <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {showRangeBg && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            height: '32px',
            left: 0,
            right: 0,
            backgroundColor: RANGE_BG,
            borderTopLeftRadius: roundLeft ? '16px' : 0,
            borderBottomLeftRadius: roundLeft ? '16px' : 0,
            borderTopRightRadius: roundRight ? '16px' : 0,
            borderBottomRightRadius: roundRight ? '16px' : 0,
            zIndex: 0,
          }}
        />
      )}
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={false}
        sx={{
          position: 'relative',
          zIndex: 1,
          fontFamily: "'Noto Sans', sans-serif !important",
          fontWeight: '700 !important',
          fontSize: '13px !important',
          borderRadius: '9px !important',
          margin: 0,
          color: outsideCurrentMonth ? '#C7C9D1 !important' : '#111827 !important',
          ...(isStart || isEnd
            ? {
              backgroundColor: `${RANGE_ACCENT} !important`,
              color: '#fff !important',
              boxShadow: '0px 4px 10px rgba(56, 114, 176, 0.25)',
              '&:hover': { backgroundColor: `${RANGE_ACCENT_HOVER} !important` },
            }
            : isInRange
              ? {
                backgroundColor: 'transparent !important',
                '&:hover': { backgroundColor: 'rgba(56, 114, 176, 0.10) !important' },
              }
              : {
                '&:hover': { backgroundColor: '#EEF0FF !important' },
              }),
        }}
      />
    </div>
  );
};

const SingleDay = ({ day, outsideCurrentMonth, ...other }) => (
  <PickersDay
    {...other}
    day={day}
    outsideCurrentMonth={outsideCurrentMonth}
    sx={{
      fontFamily: "'Noto Sans', sans-serif !important",
      fontWeight: '700 !important',
      fontSize: '13px !important',
      borderRadius: '9px !important',
      margin: 0,
      color: outsideCurrentMonth ? '#C7C9D1 !important' : '#111827 !important',
      '&:hover': { backgroundColor: '#EEF0FF !important' },
      '&.Mui-selected': {
        backgroundColor: `${RANGE_ACCENT} !important`,
        color: '#fff !important',
        boxShadow: '0px 4px 10px rgba(56, 114, 176, 0.25)',
        '&:hover': { backgroundColor: `${RANGE_ACCENT_HOVER} !important` },
        '&:focus': { backgroundColor: `${RANGE_ACCENT} !important` },
      },
    }}
  />
);

const CustomRangeCalendarHeader = ({ currentMonth, onMonthChange }) => {
  const goPrev = () => onMonthChange?.(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1), 'right');
  const goNext = () => onMonthChange?.(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1), 'left');

  const label = currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const pretty = label.charAt(0).toUpperCase() + label.slice(1);

  return (
    <div
      className="flex items-center justify-start gap-1.5 pl-1 pr-1"
      style={{ paddingTop: 8, paddingBottom: 2 }}
    >
      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous month"
        className="flex h-6 w-6 items-center justify-center rounded-md text-[#111827] hover:bg-[#F2F2F4]"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <span className="text-[15px] font-bold tracking-tight text-[#0F1122]">{pretty}</span>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next month"
        className="flex h-6 w-6 items-center justify-center rounded-md text-[#111827] hover:bg-[#F2F2F4]"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </div>
  );
};

const RANGE_CELL = 34;
const RANGE_GRID_WIDTH = RANGE_CELL * 7;
const RANGE_DAY_HEIGHT = 32;
const RANGE_ROW_HEIGHT = 34;
const RANGE_WEEK_ROWS = 6;
const RANGE_GRID_HEIGHT = RANGE_ROW_HEIGHT * RANGE_WEEK_ROWS;

const rangeCalendarSx = {
  width: RANGE_GRID_WIDTH,
  minWidth: RANGE_GRID_WIDTH,
  height: 'auto',
  maxHeight: 'none',
  padding: 0,
  '& .MuiPickersCalendarHeader-root': { padding: 0, margin: 0, minHeight: 0 },
  '& .MuiDayCalendar-root': { padding: 0 },
  '& .MuiDayCalendar-header': {
    padding: 0,
    paddingTop: '4px',
    justifyContent: 'flex-start',
    gap: 0,
  },
  '& .MuiDayCalendar-weekDayLabel': {
    fontFamily: "'Noto Sans', sans-serif",
    fontWeight: 700,
    fontSize: '10px',
    letterSpacing: '0.06em',
    color: '#A8ADBA',
    textTransform: 'uppercase',
    width: RANGE_CELL,
    minWidth: RANGE_CELL,
    height: 24,
    margin: 0,
  },
  '& .MuiPickersSlideTransition-root': {
    minHeight: RANGE_GRID_HEIGHT,
    height: RANGE_GRID_HEIGHT,
    overflow: 'hidden',
  },
  '& .MuiDayCalendar-monthContainer': { position: 'static' },
  '& .MuiDayCalendar-weekContainer': {
    margin: '1px 0',
    justifyContent: 'flex-start',
    gap: 0,
  },
  '& .MuiPickersDay-root': {
    width: RANGE_CELL,
    minWidth: RANGE_CELL,
    height: RANGE_DAY_HEIGHT,
    margin: 0,
  },
};

const RANGE_DATE_FORMATS = { dayOfMonth: 'dd', weekdayShort: 'EEE' };

const Input = ({
  type = "text",
  name = "",
  label = "",
  options = [],
  value,
  onChange,
  disabled = false,
  isNormal = false,
  placeholder = "",
  required,
  context = "form",
  get = false,
  isDashboardFilter = false,
  isPasswordConfirmation = false,
  // Dense variant for table rows (dropdown only): 40px high, no floating label - the label is shown
  // as a placeholder instead
  compact = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [startDateText, setStartDateText] = useState("");
  const [localSelectedOptions, setLocalSelectedOptions] = useState(Array.isArray(value) ? [...value] : []);

  const isLabelActive = isFocused ||
    (type === "daterange" ? (startDate !== null) :
      (type === "date" ? (showDatePicker || startDate !== null) :
        (type === "checkbox-dropdown" ? localSelectedOptions.length > 0 :
          (Array.isArray(value) ? value.length > 0 : !!value))));

  const { t } = useTranslation();
  const datePickerRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownSelectWrapperRef = useRef(null);
  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);
  const dropdownMenuPlacementRef = useRef('auto');
  const DROPDOWN_MAX_MENU_HEIGHT = 260;

  useEffect(() => {
    if (type === "checkbox-dropdown") {
      setLocalSelectedOptions(Array.isArray(value) ? [...value] : []);
    }
  }, [value, type]);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.match(/[a-z]/)) strength += 1;
    if (password.match(/[A-Z]/)) strength += 1;
    if (password.match(/[0-9]/)) strength += 1;
    if (password.match(/[^a-zA-Z0-9]/)) strength += 1;
    return strength;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (type === "password" && !isPasswordConfirmation) {
      setPasswordStrength(checkPasswordStrength(newValue));
    }
  };

  const formatDateForPayload = (date, isEndDate = false) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    
    // For date ranges: start date = 00:00:00, end date = 23:59:59
    const hours = isEndDate ? "23" : "00";
    const minutes = isEndDate ? "59" : "00";
    const seconds = isEndDate ? "59" : "00";
    
    const timezoneOffset = -date.getTimezoneOffset();
    const absOffset = Math.abs(timezoneOffset);
    const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const offsetMinutes = String(absOffset % 60).padStart(2, "0");
    const timezone = `${timezoneOffset >= 0 ? "+" : "-"}${offsetHours}${offsetMinutes}`;

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezone}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}-${month}-${year}`;
  };

  // Lenient "DD/MM/YYYY" (or "DD-MM-YYYY") parser so a date can be typed as a shortcut instead of clicked.
  const parseTypedDate = (str) => {
    if (!str) return null;
    const match = str.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
  };

  useEffect(() => {
    if (type !== "date") return;
    setStartDateText(startDate ? formatDateForDisplay(startDate) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, type]);

  const handleStartDateTextChange = (e) => {
    const raw = e.target.value;
    setStartDateText(raw);
    const parsed = parseTypedDate(raw);
    if (!parsed) return;
    handleDateSelection(parsed);
  };

  const handleDateTextBlur = () => {
    setStartDateText(startDate ? formatDateForDisplay(startDate) : "");
  };

  const blurOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.target.blur();
    }
  };

  const formatDateForPayloadGet = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}/${month}/${year}`;
  };

  const formatDateBasedOnGetFlag = (date, isEndDate = false) => {
    if (!date) return "";
    return get ? formatDateForPayloadGet(date) : formatDateForPayload(date, isEndDate);
  };

  const handleDateSelection = (date) => {
    if (type === "daterange") {
      if (selectingStart) {
        setStartDate(date);
        setEndDate(null);
        setSelectingStart(false);
      } else if (date >= startDate) {
        setEndDate(date);
        setSelectingStart(true);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    } else if (type === "date") {
      setStartDate(date);
      setShowDatePicker(false);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      if (onChange) {
        onChange(`${year}-${month}-${day}`);
      }
    };
  };

  const resetSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
    setShowDatePicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && typeof value === "object") {
      if ("startDate" in value || "endDate" in value) {
        setStartDate(value.startDate ? new Date(value.startDate) : null);
        setEndDate(value.endDate ? new Date(value.endDate) : null);
        setSelectingStart(!value.startDate || !!value.endDate);
        return;
      }

      if ("date" in value) {
        const parsedDate = new Date(value.date);
        if (!isNaN(parsedDate.getTime())) {
          setStartDate(parsedDate);
        }
        return;
      }
    } else {
      resetSelection();
    }

    if (typeof value === "string") {
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        setStartDate(parsedDate);
      }
    } else {
      resetSelection();
    }
  }, [value]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#ffffff",
      border: state.isFocused ? "1px solid #3872B0" : "1px solid #E3E3E3",
      borderRadius: "8px",
      height: compact ? "40px" : "48px",
      minHeight: compact ? "40px" : provided.minHeight,
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      fontSize: compact ? "14px" : "16px",
      fontWeight: 500,
      paddingLeft: compact ? "4px" : "19px",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #3872B0",
      },

    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: compact ? "0 8px" : "8px 0",
    }),

    singleValue: (provided) => ({
      ...provided,
      color: compact ? "#043F88" : "#000000",
      fontSize: compact ? "14px" : "16px",
      fontWeight: compact ? 600 : 500,
      paddingTop: compact ? "0" : "8px",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.selectProps?.context === "filter" ? "#38485C" : "#AFB0B2",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      marginTop: "4px",
      zIndex: 10000,
      overflow: "hidden",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
      maxHeight: "min(300px, 50vh)",
      overflowY: "auto",
    }),

    option: (provided, state) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 20px",
      backgroundColor: state.isSelected
        ? "#3872B0"
        : state.isFocused
          ? "#E3F1FA"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#333",
      fontSize: "16px",
      fontWeight: state.isSelected ? "bold" : "semibold",
      cursor: "pointer",
      position: "relative",

      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "10%",
        width: "80%",
        height: "0.5px",
        backgroundColor: "#E4E7EA",
      },

      "&:last-child::after": {
        display: "none",
      },
    }),

    placeholder: (provided) => ({
      ...provided,
      fontSize: compact ? "14px" : "16px",
      fontWeight: 500,
      color: compact ? "#AFB0B2" : "#000000",
      paddingTop: compact ? "0" : "8px",
    }),

  };

  const renderInputField = () => {
    switch (type) {
      case "hidden":
        return (
          <input type="hidden" name={name} value={value || ""} />
        );
      
      case "checkbox-dropdown":
        const allOptionValue = "SELECT_ALL";

        const handleCheckboxChange = (optionValue, isChecked) => {
          let updatedValue = [...localSelectedOptions];

          if (optionValue === allOptionValue) {
            updatedValue = isChecked ? options.map((opt) => opt.value) : [];
          } else {
            if (isChecked) {
              updatedValue.push(optionValue);
            } else {
              updatedValue = updatedValue.filter((val) => val !== optionValue);
            }

            const allSelected = options.every((opt) => updatedValue.includes(opt.value));
            updatedValue = allSelected
              ? [allOptionValue, ...updatedValue]
              : updatedValue.filter((val) => val !== allOptionValue);
          }

          setLocalSelectedOptions(updatedValue);
        };

        const handleApply = () => {
          onChange(localSelectedOptions);
          setIsDropdownOpen(false);
        };

        const getDisplayText = () => {
          const selectedOptions = localSelectedOptions.filter((v) => v !== allOptionValue);
          if (localSelectedOptions.includes(allOptionValue)) {
            return "Tous";
          } else if (selectedOptions.length === 0) {
            return "";
          } else if (selectedOptions.length <= 2) {
            return selectedOptions
              .map((val) => options.find((opt) => opt.value === val)?.label || val)
              .join(", ");
          } else {
            return `${selectedOptions.length} ${t("placeholder.selected")}`;
          }
        };


        const MenuList = (props) => (
          <components.MenuList {...props} className="relative">
            <div className="overflow-y-auto max-h-[260px]">
              {props.children}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#E4E7EA]">
              <button
                onClick={handleApply}
                className="w-full pr-[21px] py-[10px] text-right text-[14px] font-bold text-primary"
              >
                {t("actions.apply")}
              </button>
            </div>
          </components.MenuList>
        );

        return (
          <Select
            options={[
              ...(isDashboardFilter
                ? [{
                  value: allOptionValue,
                  label: (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={localSelectedOptions.includes(allOptionValue)}
                        onChange={(e) => handleCheckboxChange(allOptionValue, e.target.checked)}
                        className="mr-2 cursor-pointer w-4 h-4 rounded border border-gray-300"
                        style={{ accentColor: "#3872B0" }}
                      />
                      {localSelectedOptions.includes(allOptionValue)
                        ? t("placeholder.unselectAll")
                        : t("placeholder.selectAll")}
                    </div>
                  ),
                }]
                : []),
              ...options
                .filter((opt) => opt.value !== allOptionValue)
                .map((option) => ({
                  value: option.value,
                  label: (
                    <div
                      className="flex items-center cursor-pointer w-full"
                      onClick={() => handleCheckboxChange(option.value, !localSelectedOptions.includes(option.value))}
                    >
                      <input
                        type="checkbox"
                        checked={localSelectedOptions.includes(option.value)}
                        onChange={(e) => e.stopPropagation()}
                        className="mr-2 cursor-pointer w-4 h-4 rounded border border-gray-300"
                        style={{ accentColor: "#3872B0" }}
                      />
                      {option.label || option.name}
                    </div>
                  ),
                })),
            ]}
            value={[]}
            placeholder={getDisplayText()}
            styles={{
              ...customSelectStyles,
              singleValue: (provided) => ({
                ...provided,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }),
            }}
            isDisabled={disabled}
            components={{ IndicatorSeparator: null, MenuList }}
            menuIsOpen={isDropdownOpen}
            onMenuOpen={() => setIsDropdownOpen(true)}
            onMenuClose={() => setIsDropdownOpen(false)}
            closeMenuOnSelect={false}
            context={context}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={260}
          />
        );

      case "dropdown":
        return (
          <div ref={dropdownSelectWrapperRef}>
            <Select
              options={options.map((option) => {
              if (typeof option === "string") {
                return { value: option, label: option };
              } else if (option.reference || option.name) {
                return {
                  value: option.id || option.value,
                  label: (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {option.reference && (
                        <span style={{ color: "#BFD6EB", fontWeight: "bold", fontSize: "14px" }}>
                          {option.reference}
                        </span>
                      )}
                      {option.name && (
                        <span style={{ color: "#fffff", fontSize: "12px" }}>
                          {option.name}
                        </span>
                      )}
                    </div>
                  ),
                  reference: option.reference,
                  name: option.name,
                };
              } else {
                return {
                  value: option.id || option.value,
                  label:
                    option.firstName && option.lastName
                      ? `${option.firstName} ${option.lastName}`
                      : option.label || option.name,
                };
              }
            })}
            value={(() => {
              const selectedOption = options.find(
                (option) => String(option.value) === String(value)
              );
              if (selectedOption?.reference && selectedOption?.name) {
                return { value, label: selectedOption.name };
              }
              return (
                options.find(
                  (option) =>
                    String(option.id || option.value) === String(value.id || value)
                ) || null
              );
            })()}
            onChange={(selectedOption) =>
              onChange && onChange(selectedOption ? selectedOption.value : null)
            }
            placeholder={compact ? label : ""}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            styles={customSelectStyles}
            isDisabled={disabled}
            context={context}
            noOptionsMessage={() => t?.("placeholder.noResults")}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            menuPlacement={dropdownMenuPlacementRef.current}
            menuIsOpen={dropdownMenuOpen}
            onMenuOpen={() => {
              const control = dropdownSelectWrapperRef.current?.querySelector('[class*="control"]');
              const rect = control?.getBoundingClientRect();
              if (rect) {
                const spaceBelow = window.innerHeight - rect.bottom;
                dropdownMenuPlacementRef.current = spaceBelow < DROPDOWN_MAX_MENU_HEIGHT ? 'top' : 'bottom';
              }
              setDropdownMenuOpen(true);
            }}
            onMenuClose={() => setDropdownMenuOpen(false)}
            maxMenuHeight={DROPDOWN_MAX_MENU_HEIGHT}
            isSearchable
            filterOption={createFilter({
              ignoreCase: true,
              trim: true,
              matchFrom: "any",
              stringify: (option) => {
                const d = option.data || {};
                if (d.reference || d.name) {
                  return `${d.reference ?? ""} ${d.name ?? ""}`.trim();
                }
                if (typeof d.label === "string") return d.label;
                if (d.firstName || d.lastName) {
                  return `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim();
                }
                return String(d.value ?? "");
              },
            })}
            />
          </div>
        );

      case "search":
        return (
          <div className="relative w-full h-[50px]">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A3A3A3]">
              <img src={searchIcon} alt="search" className="w-5 h-5" />
            </span>
            <input
              type="search"
              className="w-full h-[50px] pl-10  pr-[8px] text-[16px] font-medium placeholder-[#A3A3A3] text-black 
             border border-[#E3E3E3] rounded-lg 
             focus:border-primary hover:border-primary focus:outline-none"
              value={value ?? ""}
              onChange={(e) => onChange && onChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={disabled}
              placeholder={t(`input.search`)}
            />
          </div>
        );

      case "toggle":
        const selectedOption = options.find((option) => option.value === value);
        const nextOption = options.find((option) => option.value !== value);

        return (
          <button
            onClick={() => nextOption && onChange(nextOption.value)}
            className={`flex items-center justify-center w-[50px] h-[50px] rounded-lg border border-[#E3E3E3] bg-white`}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            disabled={disabled}
            title={selectedOption?.tooltip || ''}
          >
            {selectedOption?.icon ? (
              <img
                src={selectedOption.icon}
                alt={selectedOption.tooltip || ''}
                width={24}
                height={24}
              />
            ) : (
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#E3E3E3',
                }}
              />
            )}
          </button>
        );
        
      case "daterange":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr} dateFormats={RANGE_DATE_FORMATS}>
            <div className="relative w-full" ref={datePickerRef}>
              <div
                className="w-full h-12 px-4 flex items-center justify-between bg-white border border-[#E3E3E3] rounded-lg cursor-pointer hover:border-primary"
                onClick={() => setShowDatePicker(true)}
              >
                <span
                  className={`text-gray-700 text-[14px] font-semibold pl-[4px] ${label ? "pt-[12px]" : ""
                    }`}
                >
                  <span className="inline-flex flex-wrap">
                    {startDate && endDate
                      ? <>
                        <span>{formatDateForDisplay(startDate)}</span>
                        <span className="px-1">-</span>
                        <span>{formatDateForDisplay(endDate)}</span>
                      </>
                      : startDate
                        ? formatDateForDisplay(startDate)
                        : label
                          ? ""
                          : "Date"}
                  </span>
                </span>
                <img src={calendarIcon} alt="calendar" className="w-6 h-6" />
              </div>

              {showDatePicker && (
                <div
                  className="absolute z-50 mt-2 bg-white rounded-2xl"
                  style={{ boxShadow: '0px 12px 40px rgba(15, 17, 34, 0.10)' }}
                >
                  <div className="flex gap-3 px-4 pt-3">
                    <DateCalendar
                      value={startDate}
                      onChange={handleDateSelection}
                      showDaysOutsideCurrentMonth
                      reduceAnimations
                      slots={{ day: RangeDay, calendarHeader: CustomRangeCalendarHeader }}
                      slotProps={{ day: { startDate, endDate } }}
                      sx={rangeCalendarSx}
                    />
                    <DateCalendar
                      value={endDate}
                      onChange={handleDateSelection}
                      referenceDate={
                        endDate ||
                        (startDate
                          ? new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1)
                          : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1))
                      }
                      showDaysOutsideCurrentMonth
                      reduceAnimations
                      slots={{ day: RangeDay, calendarHeader: CustomRangeCalendarHeader }}
                      slotProps={{ day: { startDate, endDate } }}
                      sx={rangeCalendarSx}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2 px-4 pb-3 pt-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 font-semibold text-[13px] text-primary transition-colors"
                      onClick={resetSelection}
                    >
                      {t(`actions.cancel`)}
                    </button>
                    <button
                      type="button"
                      className="px-5 py-2 rounded-xl font-semibold text-[13px] text-white transition-colors"
                      style={{ backgroundColor: RANGE_ACCENT, boxShadow: '0px 8px 16px rgba(56,114,176,0.25)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RANGE_ACCENT_HOVER)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RANGE_ACCENT)}
                      onClick={() => {
                        setShowDatePicker(false);
                        onChange({
                          startDate: startDate ? formatDateBasedOnGetFlag(startDate, false) : null,
                          endDate: endDate ? formatDateBasedOnGetFlag(endDate, true) : null,
                        });
                      }}
                    >
                      {t(`actions.apply`)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </LocalizationProvider>
        );

      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr} dateFormats={RANGE_DATE_FORMATS}>
            <div className="relative w-full" ref={datePickerRef}>
              <div className="relative w-full h-[48px] border border-[#E3E3E3] rounded-lg bg-white hover:border-primary focus-within:border-primary">
                <input
                  type="text"
                  value={startDateText}
                  onChange={handleStartDateTextChange}
                  onBlur={handleDateTextBlur}
                  onFocus={() => setShowDatePicker(true)}
                  onKeyDown={blurOnEnter}
                  placeholder={isLabelActive ? "JJ-MM-AAAA" : ""}
                  className="w-full h-full bg-transparent outline-none text-[16px] font-medium text-[#000000] pl-[21px] pr-11 pt-[22px] pb-[8px] placeholder:text-[#AFB0B2] placeholder:font-medium"
                />

                <img
                  src={calendarIcon}
                  alt="calendar"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 pointer-events-none"
                  style={{
                    filter: "brightness(0) saturate(100%) invert(75%) sepia(4%) saturate(8%) hue-rotate(180deg)",
                  }}
                />
              </div>

              {showDatePicker && (
                <div
                  className="absolute z-50 mt-2 bg-white rounded-2xl"
                  style={{ boxShadow: '0px 12px 40px rgba(15, 17, 34, 0.10)' }}
                >
                  <div className="px-4 pt-3">
                    <DateCalendar
                      value={startDate}
                      onChange={(date) => {
                        handleDateSelection(date);
                      }}
                      showDaysOutsideCurrentMonth
                      reduceAnimations
                      slots={{ day: SingleDay, calendarHeader: CustomRangeCalendarHeader }}
                      sx={rangeCalendarSx}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2 px-4 pb-3 pt-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 font-semibold text-[13px] text-primary transition-colors"
                      onClick={resetSelection}
                    >
                      {t(`actions.cancel`)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </LocalizationProvider>
        );

      case "password":
        return (
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={value ?? ""}
              onChange={(e) => {
                handleChange(e);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`w-full h-[48px] px-[21px] border border-[#E3E3E3] rounded-lg pt-[22px] pb-[8px]
                  focus:border-primary focus:outline-none text-[16px] font-medium bg-white text-[#000000]`}
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-4 text-gray-500 text-sm"
            >
              <img
                src={showPassword ? EyeOffIcon : EyeIcon}
                alt="Toggle password visibility"
                className="w-5 h-5"
              />
            </button>

            {!isPasswordConfirmation && value.length > 0 && (
              <div className="mt-2 h-2 w-full bg-gray-200 rounded-lg relative">
                <div
                  className="absolute h-2 rounded-lg transition-all duration-300"
                  style={{
                    width: `${(passwordStrength / 5) * 100}%`,
                    backgroundColor:
                      passwordStrength <= 2 ? "red" :
                        passwordStrength === 3 ? "orange" :
                          passwordStrength >= 4 ? "green" : "transparent",
                  }}
                />
              </div>
            )}
          </div>
        );
      default:
        const isQuantity = name === "quantity";
        const isAmountField = name === "priceHT" || name === "priceTTC" || name === "amount";

        return (
          <input
            {...props}
            type={type}
            className={`w-full h-[48px] px-[21px] border border-[#E3E3E3] rounded-lg 
              focus:border-primary focus:outline-none hover:border-primary text-[16px] font-medium bg-white text-[#000000]
              ${isNormal ? "py-[12px]" : "pt-[22px] pb-[8px]"}
              ${props.className || ""}`}
            value={
              isAmountField && !isFocused
                ? formatAmountForm(value ?? "")
                : (value ?? "")
            }
            onChange={(e) => onChange && onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              if (isAmountField && onChange) {
                onChange(e.target.value);
              }
            }}
            min={props.min !== undefined ? props.min : (isQuantity ? "1" : 0)}
            max={props.max !== undefined ? props.max : undefined}
            disabled={disabled}
            placeholder={placeholder}
          />
        );
    }
  };

  if (type === "hidden") {
    return renderInputField();
  }

  return (
    <div
      className={`relative ${type === "toggle" ? "w-auto" : "flex-shrink-0"}`}
      style={{
        flex: '1 1 calc(15% - 1rem)',
        minWidth: compact ? 0 : '150px',

      }}
    >
      {!isNormal && !compact && label && (
        <label
          className={`absolute px-[22px] transition-all duration-200 ${isLabelActive
            ? "top-[9px] text-[8px] font-semibold text-primary"
            : `top-[14.5px] text-[14px] font-semibold ${context === "filter" ? "text-[#38485C]" : "text-[#AFB0B2]"
            }`
            }`}
          style={{
            pointerEvents: "none",
            zIndex: 10,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "90%",
          }}
          title={label}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderInputField()}
    </div>
  );
};

export default Input;
